import { observable, makeObservable } from 'mobx'
import User from 'iam/models/User'
import { PrimeUser } from 'iam/UserStore'
import Workspace from 'iam/models/Workspace'
import { Model } from 'infra/model'
import { PendingTransactionResource } from './dtos/drafting'
import {
  parseTransactionSourceType,
  TransactionSourceType,
} from 'payouts/TransactionSourceType'
import { parseTransactionType, TransactionType } from 'payouts/TransactionType'
import {
  parseUserTransactionReason,
  UserTransactionReason,
} from 'payouts/UserTransactionReason'
import { Id } from 'types/definitions'
import { DateZero, maybeParseDate } from 'utils/date-util'
import { PrimeWorkspace } from '../../iam/WorkspaceStore'
import { TransactionFlag, parseTransactionFlag } from '../TransactionFlag'
import {
  parseTransactionFailureReason,
  TransactionFailureReason,
} from '../TransactionFailureReason'

export default class PendingTransaction extends Model<
  PendingTransactionResource,
  PendingTransaction
> {
  id: Id
  shortId = ''
  amount = 0
  createdAt = DateZero()
  transactedAt = DateZero()
  type = TransactionType.Payment
  recipient = User.none
  workspace = Workspace.none
  source = TransactionSourceType.Unknown

  reason: UserTransactionReason | null = UserTransactionReason.Unknown
  memo: string | null = null

  jobId: string | null = null

  flags: TransactionFlag[] = []

  @observable iteration = 1

  @observable lastFailureReason: TransactionFailureReason | null = null
  @observable lastFailureDescription: string | null = null

  constructor(
    private primeUser: PrimeUser,
    private primeWorkspace: PrimeWorkspace,
  ) {
    super()
    makeObservable(this)
  }

  parse(
    attrs: Partial<PendingTransactionResource>,
  ): Partial<PendingTransaction> {
    return {
      id: attrs.id,
      shortId: attrs.short_id,
      amount: attrs.amount,
      createdAt: maybeParseDate(attrs.date_created),
      transactedAt: maybeParseDate(attrs.date_transacted),
      iteration: attrs.iteration,
      recipient: attrs.recipient_user_id
        ? this.primeUser({ public_id: attrs.recipient_user_id })
        : undefined,
      workspace: attrs.workspace_id
        ? this.primeWorkspace({ id: attrs.workspace_id })
        : undefined,
      type: attrs.type && parseTransactionType(attrs.type),
      source: attrs.source && parseTransactionSourceType(attrs.source),
      reason: attrs.reason && parseUserTransactionReason(attrs.reason),
      memo: attrs.memo,
      jobId: attrs.job_id,
      flags: attrs.flags?.map(parseTransactionFlag),
      lastFailureReason:
        attrs.last_failure_reason &&
        parseTransactionFailureReason(attrs.last_failure_reason),
      lastFailureDescription: attrs.last_failure_description,
    }
  }
}
